/**
 * Feature Flag Resolution Utilities
 * 
 * Core resolution logic shared between client and server.
 * Implements the priority: killSwitch > org > system > default
 */

import type { FeatureFlagKey } from "../constants/feature-flags";
import type {
  FeatureFlagValue,
  OrgFeatureFlag,
  SystemFeatureFlag,
} from "../types/feature-flags";

/**
 * Resolve a feature flag value based on priority:
 * 1. Kill switch (on system flag) - if true, feature is disabled
 * 2. Org-level flag - if exists, use this
 * 3. System-level flag - if exists, use this
 * 4. Code default - fallback to default value
 * 
 * @param flagKey - The feature flag key
 * @param systemFlag - System-level flag from Firestore (may be null/undefined)
 * @param orgFlag - Org-level flag from Firestore (may be null/undefined)
 * @param defaultValue - Default value from code definition
 * @returns Resolved feature flag value with source tracking
 */
export function resolveFeatureFlag(
  flagKey: FeatureFlagKey,
  systemFlag: SystemFeatureFlag | null | undefined,
  orgFlag: OrgFeatureFlag | null | undefined,
  defaultValue: boolean | string | number | object,
): FeatureFlagValue {
  // Priority 1: Kill switch (on system flag)
  if (systemFlag?.killSwitch === true) {
    return {
      enabled: false,
      value: null,
      source: "killSwitch",
    };
  }

  // Priority 2: Org-level flag
  if (orgFlag) {
    return {
      enabled: orgFlag.enabled,
      value: orgFlag.value !== undefined ? orgFlag.value : defaultValue,
      source: "org",
    };
  }

  // Priority 3: System-level flag
  if (systemFlag) {
    return {
      enabled: systemFlag.enabled,
      value: systemFlag.value !== undefined ? systemFlag.value : defaultValue,
      source: "system",
    };
  }

  // Priority 4: Code default
  return {
    enabled: typeof defaultValue === "boolean" ? defaultValue : true,
    value: defaultValue,
    source: "default",
  };
}

/**
 * Check if a feature flag is enabled (convenience function)
 * 
 * @param resolvedFlag - Resolved feature flag value
 * @returns true if the feature is enabled
 */
export function isFeatureEnabled(
  resolvedFlag: FeatureFlagValue,
): boolean {
  return resolvedFlag.enabled;
}

/**
 * Get the feature flag value (convenience function)
 * 
 * @param resolvedFlag - Resolved feature flag value
 * @returns The resolved value
 */
export function getFeatureFlagValue(
  resolvedFlag: FeatureFlagValue,
): any {
  return resolvedFlag.value;
}

